import React, { useContext, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  Typography
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as Yup from "yup";
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper'
import { format, isValid } from 'date-fns'
import DatePickerComponent from '../../components/CustomFields/DatePicker'
import dayjs from 'dayjs';
import { CustomTextField } from '../../components/CustomFields/TextField'
import { calculateBMI, useQuery } from '../../shared/CommonFunction'
import { createInputPropsForNumberField } from '../../shared/data'
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import { CreateMemberPhysicalGrowth, GetMemberPhysicalGrowthDetail, UpdateMemberPhysicalGrowth } from '../../services/models/GrowthChart'

interface GrowthDataRow {
  patientId: number;
  opdId: number;
  memberPhysicalGrowthId: number;
  familyId: number;
  familyMemberId: number;
  entryDate: Date | null;
  height: number | null;
  weight: number | null;
  bmi: number | null;
  headCircumference: number | null;
  expectedHeight: number | null;
  isDeleted: boolean;
}

const GrowthChartItemSave = (Props: any) => {
  // Props
  const { opd, isForPopUp, handleClosePopUp, memberPhysicalGrowthId } = Props;

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const weightProps = createInputPropsForNumberField(3, 3, true);
  const heightProps = createInputPropsForNumberField(3, 1, true);
  const headCircumferenceProps = createInputPropsForNumberField(3, 1, true);
  const edService = new EncryptDecryptService();

  const initGrowthChartData: GrowthDataRow = {
    patientId: opd?.patientId || 0,
    opdId: 0,
    memberPhysicalGrowthId: memberPhysicalGrowthId || 0,
    familyId: 0,
    familyMemberId: 0,
    entryDate: new Date(Date.now()),
    height: null,
    weight: null,
    bmi: null,
    headCircumference: null,
    expectedHeight: null,
    isDeleted: false,
  }

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    patientId: Yup.number().nullable(),
    entryDate: Yup.date()
      .nullable()
      .required("Date is required"),
    weight: Yup.number()
      .max(999, "Max weight limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    height: Yup.number()
      .max(999, "Max height limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    headCircumference: Yup.number()
      .max(999, "Max headCircumference limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    expectedHeight: Yup.number()
      .max(999, "Max Expected Height limit is 999")
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      ),
    isDeleted: Yup.boolean(),
  });

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawMemberPhysicalGrowthId = urlParams.get("q");
  let curMemberPhysicalGrowthId = 0;

  if (!isForPopUp) {
    if (curRawMemberPhysicalGrowthId !== null && curRawMemberPhysicalGrowthId !== undefined) {
      let decId = edService.decrypt(curRawMemberPhysicalGrowthId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        curMemberPhysicalGrowthId = +decId;
      }
    }
  } else {
    if (isForPopUp && opd?.patientId > 0) {
      curMemberPhysicalGrowthId = memberPhysicalGrowthId;
    }
  }

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initGrowthChartData,
  });

  // GET MEMBER PHYSICAL GROWTH CHART DETAIL
  const getMemberPhysicalGrowthDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("memberPhysicalGrowthId", memberPhysicalGrowthId?.toString());
    // params.append("familyId", memberPhysicalGrowthId?.toString());
    // params.append("familyMemberId", memberPhysicalGrowthId?.toString());

    try {

      const res: any = await GetMemberPhysicalGrowthDetail(params);
      item = res.data.data;
      methods.reset({
        ...initGrowthChartData,
        memberPhysicalGrowthId: item?.memberPhysicalGrowthId || 0,
        patientId: item?.patientId || 0,
        opdId: item?.opdId || 0,
        familyId: item?.familyId || 0,
        familyMemberId: item?.familyMemberId || 0,
        entryDate: item?.entryDate ? new Date(item.entryDate) : null,
        height: item?.height || 0,
        weight: item?.weight || 0,
        bmi: calculateBMI(item?.height || 0, item?.weight || 0),
        headCircumference: item?.headCircumference || 0,
        expectedHeight: item?.expectedHeight || 0,
      });
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const saveGrowthData = async (value: GrowthDataRow) => {
    try {

      let result: any = "";
      let formData = {
        memberPhysicalGrowthId: value?.memberPhysicalGrowthId || 0,
        patientId: value?.patientId || 0,
        opdId: value?.opdId || 0,
        appointmentId: opd?.appointmentId || 0,
        branchId: userContext?.branchId || 0,
        orgId: userContext?.organizationId || 0,
        consumerId: userContext?.consumerId || 0,
        familyId: value?.familyId || 0,
        familyMemberId: value?.familyMemberId || 0,
        entryDate: value?.entryDate ? format(new Date(value.entryDate), "yyyy-MM-dd") : null,
        height: value?.height || 0,
        weight: value?.weight || 0,
        bmi: calculateBMI(value?.height || 0, value?.weight || 0),
        headCircumference: value?.headCircumference || 0,
        expectedHeight: value?.expectedHeight || 0,
        dataManagedBy: userContext.loginDetail.userId
      };

      if (memberPhysicalGrowthId > 0) {
        result = await UpdateMemberPhysicalGrowth(formData)
      } else {
        result = await CreateMemberPhysicalGrowth(formData);
      }

      if (result?.data) {
        if (isForPopUp) {
          handleClosePopUp();
        } else {
          navigate(-1);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await saveGrowthData(data);
    setIsLoading(false);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curMemberPhysicalGrowthId > 0) {
        await getMemberPhysicalGrowthDetail();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curMemberPhysicalGrowthId]);

  return (
    <>
      <Box className={"main-container"}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="" autoComplete='off'>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className="fileds-label-text"
                  >
                    Date
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input"
                  >
                    <DatePickerComponent
                      format="DD-MM-YYYY"
                      mode="date"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      }}
                      value={
                        methods?.getValues()?.entryDate
                          ? dayjs(methods?.getValues()?.entryDate)
                          : null
                      }
                      onChange={(e: any) => {
                        if (e !== null && isValid(e.$d)) {
                          methods.setValue("entryDate", e.$d, { shouldValidate: true });
                        } else {
                          methods.setValue("entryDate", null, { shouldValidate: true });
                        }
                      }}
                    />
                  </SkeletonWrapper>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}></Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className='fileds-label-text'
                  >
                    Weight
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input">
                    <CustomTextField
                      placeholder="000.000"
                      id="weight"
                      size="small"
                      inputProps={{
                        ...weightProps,
                      }}
                      {...methods.register("weight")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            Kg
                          </InputAdornment>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className='fileds-label-text'
                  >
                    Height
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input">
                    <CustomTextField
                      placeholder="000.000"
                      id="height"
                      size="small"
                      inputProps={{
                        ...heightProps,
                      }}
                      {...methods.register("height")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            cm
                          </InputAdornment>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className='fileds-label-text'
                  >
                    HC
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input">
                    <CustomTextField
                      placeholder="000.000"
                      id="headCircumference"
                      size="small"
                      inputProps={{
                        ...headCircumferenceProps,
                      }}
                      {...methods.register("headCircumference")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            cm
                          </InputAdornment>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="center"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    loading={isLoading}
                  >
                    {memberPhysicalGrowthId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="center"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                    disabled={isLoading}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </>
  )
}

export default GrowthChartItemSave
